import { useEffect, useState } from "react";

interface CountdownProps {
  /** ISO date string, e.g. "2026-08-15" */
  target: string;
  label?: string;
  className?: string;
}

function diff(target: number, now: number) {
  const ms = Math.max(0, target - now);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms / 3_600_000) % 24);
  const minutes = Math.floor((ms / 60_000) % 60);
  const seconds = Math.floor((ms / 1000) % 60);
  return { ms, days, hours, minutes, seconds };
}

/** Live countdown to a release date. Hides itself if the target is invalid or past. */
export function Countdown({ target, label = "Releases in", className }: CountdownProps) {
  const targetTime = new Date(target + (target.length === 10 ? "T00:00:00Z" : "")).getTime();
  // Start at null so SSR and the first client render match exactly; the live
  // clock only starts after hydration (avoids a seconds/minutes mismatch).
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    if (!Number.isFinite(targetTime)) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [targetTime]);

  if (!Number.isFinite(targetTime)) return null;
  const d = diff(targetTime, now ?? Date.now());
  if (now !== null && d.ms <= 0) return null;

  const items = [
    { label: "Days", value: d.days },
    { label: "Hours", value: d.hours },
    { label: "Minutes", value: d.minutes },
    { label: "Seconds", value: d.seconds },
  ];


  return (
    <div className={className ?? "inline-flex flex-col gap-2"} role="timer" aria-label={label}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</p>
      <ol className="grid grid-cols-4 gap-2 sm:gap-3">
        {items.map((it) => (
          <li
            key={it.label}
            className="rounded-md border hairline bg-charcoal px-2 py-2 text-center sm:px-3 sm:py-3"
          >
            <span className="block font-serif text-xl tabular-nums text-gold-luminous sm:text-2xl">
              {now === null ? "--" : it.value.toString().padStart(2, "0")}
            </span>

            <span className="mt-1 block text-[9px] uppercase tracking-[0.25em] text-muted-foreground sm:text-[10px]">
              {it.label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
